import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { SampleJourney } from "@/components/SampleJourney";
import { FieldHint } from "@/components/FieldHint";
import {
  Target, Users, Compass, Lightbulb, FileText, Megaphone, CalendarDays, Package,
  ArrowRight, ArrowLeft, RefreshCw, Loader2, Copy, Check, Sparkles, Gift, PartyPopper,
  ShoppingBag, Trophy, CheckCircle2, Pencil, Mail, Youtube, Instagram, Music2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const BRAND_CONFETTI = ["#1E63C2", "#3BA776", "#E0A93B", "#5B8FE0"];
function fireConfetti() {
  try {
    confetti({ particleCount: 70, spread: 72, startVelocity: 38, origin: { y: 0.28 },
      colors: BRAND_CONFETTI, scalar: 0.9, ticks: 160, disableForReducedMotion: true });
  } catch (e) { console.debug("Confetti skipped:", e); }
}

// Warm, first-time-only wins (journey_complete celebrates every finish).
const WIN_COPY = {
  first_step: { icon: Sparkles, title: "First step done", msg: "You've locked in a real opportunity to build on. This is the hard part — starting." },
  first_hook: { icon: Lightbulb, title: "Your first hook", msg: "That's the line that stops the scroll. You just wrote something people will actually stop for." },
  first_pitch: { icon: Trophy, title: "Pitch test passed", msg: "Your offer holds up under pressure — that's a genuine milestone most people never reach.", big: true },
  journey_complete: { icon: PartyPopper, title: "You did it", msg: "Your whole plan is ready to launch. Take a second — you earned this one.", big: true },
};

function WinToast({ meta, name }) {
  const Icon = meta.icon;
  return (
    <div data-testid="win-toast"
      className="flex w-full max-w-sm items-start gap-3 rounded-2xl border border-accent/30 bg-card p-4 shadow-lg ring-1 ring-accent/10">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="font-head text-sm font-bold">{meta.title}{name ? `, ${name}` : ""}!</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{meta.msg}</p>
      </div>
    </div>
  );
}

// Which milestones the user has completed so far, in journey order.
const MILESTONE_ORDER = [
  { key: "earn_goal", label: "Goal set" },
  { key: "audience", label: "Audience defined" },
  { key: "affiliate_product", label: "Product added" },
  { key: "opportunity", label: "Opportunity found" },
  { key: "hook", label: "Hook created" },
  { key: "script", label: "Script ready" },
  { key: "pitch_test", label: "Pitch Test passed" },
  { key: "content_plan", label: "Content plan built" },
  { key: "offer", label: "Offer packaged" },
];
function completedMilestones(data) {
  return MILESTONE_ORDER.filter((m) => {
    const v = data[m.key];
    return typeof v === "string" ? v.trim() : !!v;
  });
}

function JourneyProgress({ steps, stepIdx, data, completed, onEdit }) {
  const total = steps.length;
  const current = completed ? total : Math.min(stepIdx + 1, total);
  const pct = Math.round((current / total) * 100);
  const remaining = Math.max(0, total - current);
  const done = completedMilestones(data);
  return (
    <div className="fade-up mb-6 rounded-2xl border border-border bg-card/60 p-4 sm:p-5" data-testid="journey-progress-tracker">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" data-testid="journey-progress-label">
          {completed ? `All ${total} steps complete` : `Step ${current} of ${total}`}
        </p>
        <span className="text-xs font-medium text-muted-foreground" data-testid="journey-progress-remaining">
          {completed ? "You're done" : remaining === 0 ? "Last step" : `${remaining} to go`}
        </span>
      </div>
      <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }} data-testid="journey-progress-bar" />
      </div>
      {done.length > 0 ? (
        <div className="mt-3.5 flex flex-wrap gap-1.5" data-testid="journey-progress-milestones">
          {done.map((m) => (
            <span key={m.key} data-testid={`journey-milestone-${m.key}`}
              className="inline-flex items-center gap-1 rounded-full bg-accent/10 py-1 pl-2.5 pr-1.5 text-xs font-medium text-accent">
              <CheckCircle2 size={12} /> {m.label}
              {onEdit && (
                <button type="button" onClick={() => onEdit(m.key)} data-testid={`journey-edit-${m.key}`}
                  title={`Edit — ${m.label}`}
                  className="ml-0.5 rounded-full p-1 text-accent/70 transition-colors hover:bg-accent/20 hover:text-accent">
                  <Pencil size={11} />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">Let's get your first win on the board.</p>
      )}
    </div>
  );
}

const STEPS = ["earn", "audience", "opportunity", "hook", "script", "pitch_test", "content_plan", "offer"];
// The affiliate path adds a dedicated "affiliate" step (product + problem) after audience.
const AFFILIATE_STEPS = ["earn", "audience", "affiliate", "opportunity", "hook", "script", "pitch_test", "content_plan", "offer"];
const AI_STEPS = ["opportunity", "hook", "script", "pitch_test", "content_plan", "offer"];
const STEP_META = {
  earn: { label: "Your goal", icon: Target, title: "Set Your Goal",
    desc: "Pick what you want this to achieve — it's the foundation we build your whole course and content around." },
  audience: { label: "Your audience", icon: Users, title: "Know Your Audience",
    desc: "Describe the exact person you want to help, so everything we create speaks directly to them." },
  affiliate: { label: "Your product", icon: ShoppingBag, title: "Add Your Product",
    desc: "Tell us about the product you're promoting so we can build honest content that helps it sell." },
  opportunity: { label: "Best opportunity", icon: Compass, title: "Find Your Opportunity",
    desc: "We turn your idea into the single strongest, most sellable angle — the thing people will actually pay for." },
  hook: { label: "Your hook", icon: Lightbulb, title: "Find Your Hook",
    desc: "This is the opening line that stops the scroll — get it right and people stop and pay attention." },
  script: { label: "Your script", icon: FileText, title: "Write Your Script",
    desc: "A ready-to-record script for your first short video, so you never have to stare at a blank page." },
  pitch_test: { label: "Pitch test", icon: Megaphone, title: "Test Your Pitch",
    desc: "We stress-test your offer against real buyer doubts, so you know it holds up before you try to sell it." },
  content_plan: { label: "Content plan", icon: CalendarDays, title: "Plan Your Content",
    desc: "A simple 7-day posting plan, so you always know what to share each day and when to promote." },
  offer: { label: "Your offer", icon: Package, title: "Package Your Offer",
    desc: "We wrap it all into a clear, priced offer that's ready to sell — this is your finished product." },
};
const EARN_GOALS = [
  { id: "first_100", label: "Get my first £100 online" },
  { id: "grow_audience", label: "Grow my audience" },
  { id: "sell_product", label: "Sell a digital product" },
  { id: "get_leads", label: "Get leads for my business" },
  { id: "promote_affiliate", label: "Promote Affiliate Marketing" },
  { id: "other", label: "Something else" },
];

const AD_PLATFORMS = [
  { key: "youtube", label: "YouTube ad", icon: Youtube },
  { key: "instagram", label: "Instagram ad", icon: Instagram },
  { key: "tiktok", label: "TikTok ad", icon: Music2 },
];

// Friendly, concrete next-actions shown on the transition screen for users who
// are happy with their Guided plan and don't want to dive into Advanced yet.
function whatToDoNext(goal) {
  const base = [
    { title: "Record your first short video", desc: "Open with your hook, then follow your script. Don't overthink it — done beats perfect." },
    { title: "Post it, then keep the momentum", desc: "Publish it today and work through your 7-day content plan one post at a time." },
  ];
  const byGoal = {
    sell_product: { title: "Set up somewhere to sell", desc: "Put your packaged offer on a simple checkout or landing page so people can actually buy." },
    promote_affiliate: { title: "Share your affiliate link everywhere", desc: "Drop your link in every caption and your bio so viewers can grab the product you recommend." },
    get_leads: { title: "Start capturing leads", desc: "Point your call-to-action at a simple opt-in so interested people can leave their email." },
    grow_audience: { title: "Reply to everyone", desc: "Answer every comment and DM fast — that's what turns first-time viewers into loyal followers." },
    first_100: { title: "Make your first offer", desc: "Tell your audience exactly how to work with you and what it costs — don't wait to feel ready." },
    other: { title: "Share your offer", desc: "Tell people what you've built and the simplest way for them to get it." },
  };
  return [...base, byGoal[goal] || byGoal.other];
}

function CopyBtn({ text, id }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setDone(true); toast.success("Copied!"); setTimeout(() => setDone(false), 1500);
  };
  return (
    <button onClick={copy} data-testid={`copy-${id}`} title="Copy"
      className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
      {done ? <Check size={15} className="text-accent" /> : <Copy size={15} />}
    </button>
  );
}

export default function Journey() {
  const navigate = useNavigate();
  const { user, setUiMode, refreshCredits, refreshProfile, refreshActiveProject } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [showNext, setShowNext] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceInput, setPriceInput] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const [data, setData] = useState({});
  const [isFree, setIsFree] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [userName, setUserName] = useState("");
  // inputs
  const [earnGoal, setEarnGoal] = useState("");
  const [earnLabel, setEarnLabel] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  const [sampleMode, setSampleMode] = useState(false);
  const [audience, setAudience] = useState("");
  const [affiliateProduct, setAffiliateProduct] = useState("");
  const [affiliateProblem, setAffiliateProblem] = useState("");
  const [affiliateLink, setAffiliateLink] = useState("");
  const genGuard = useRef({});

  const loadJourney = async () => {
    const { data: d } = await api.get("/journey");
    const dd = d.journey.data || {};
    setData(dd);
    setIsFree(d.journey.is_free);
    setCompleted(d.journey.completed);
    setUserName(d.user_name || "");
    setEarnGoal(dd.earn_goal || "");
    setEarnLabel(dd.earn_goal_label || "");
    setAudience(dd.audience || d.offer?.audience || "");
    setAffiliateProduct(dd.affiliate_product || "");
    setAffiliateProblem(dd.affiliate_problem || "");
    setAffiliateLink(dd.affiliate_link || "");
    // Backend stores step as an index into the canonical STEPS; map it to this
    // journey's step list (affiliate path has one extra step).
    const localSteps = dd.earn_goal === "promote_affiliate" ? AFFILIATE_STEPS : STEPS;
    const canonName = STEPS[d.journey.step || 0] || "earn";
    setStepIdx(Math.max(0, localSteps.indexOf(canonName)));
    genGuard.current = {};
  };

  useEffect(() => {
    loadJourney().finally(() => setLoading(false));
    // Runs once on mount; loadJourney is stable for this screen's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = earnGoal === "promote_affiliate" ? AFFILIATE_STEPS : STEPS;
  const stepKey = steps[stepIdx];
  const [pendingRegen, setPendingRegen] = useState(null); // {fromStep, isAi}
  const confirmingRef = useRef(false);

  // Milestone chip key → the journey step to jump to.
  const MILESTONE_TO_STEP = { earn_goal: "earn", audience: "audience", affiliate_product: "affiliate" };
  const stepForMilestone = (k) => MILESTONE_TO_STEP[k] || k;

  const downstreamExists = (fromStepKey) => {
    const i = steps.indexOf(fromStepKey);
    if (i < 0) return false;
    return steps.slice(i + 1).some((k) => AI_STEPS.includes(k) && !!data[k]);
  };

  const jumpToStep = (targetStepKey) => {
    const idx = steps.indexOf(targetStepKey);
    if (idx < 0) return;
    if (completed) setCompleted(false);
    setStepIdx(idx);
    const canonIdx = STEPS.indexOf(targetStepKey);
    if (canonIdx >= 2) api.post("/journey/goto", { step: canonIdx }).catch(() => {});
  };

  const winsKey = `h2i_wins_${user?.id || user?.email || "anon"}`;
  const celebrate = (key) => {
    const meta = WIN_COPY[key];
    if (!meta) return;
    const oncePerUser = key !== "journey_complete";
    if (oncePerUser) {
      let fired = [];
      try { fired = JSON.parse(localStorage.getItem(winsKey) || "[]"); } catch { fired = []; }
      if (fired.includes(key)) return;
      fired.push(key);
      try { localStorage.setItem(winsKey, JSON.stringify(fired)); } catch (e) { console.warn("Could not save journey wins to localStorage:", e); }
    }
    toast.custom(() => <WinToast meta={meta} name={userName} />, { duration: 4500 });
    if (meta.big) fireConfetti();
  };

  const generate = async (key) => {
    const wasEmpty = !data[key];
    setBusy(true);
    try {
      const { data: r } = await api.post(`/journey/generate/${key}`);
      setData((d) => ({ ...d, [key]: r.artifact }));
      if (!isFree) refreshCredits();
      if (wasEmpty) {
        if (key === "opportunity") celebrate("first_step");
        else if (key === "hook") celebrate("first_hook");
        else if (key === "pitch_test") celebrate("first_pitch");
      }
    } catch (e) {
      genGuard.current[key] = false; // allow retry
      if (e.response?.status !== 402)
        toast.error(e.response?.data?.detail || "That step failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Auto-generate the current AI step the first time we land on it.
  useEffect(() => {
    if (loading || completed) return;
    const localSteps = earnGoal === "promote_affiliate" ? AFFILIATE_STEPS : STEPS;
    const key = localSteps[stepIdx];
    if (AI_STEPS.includes(key) && !data[key] && !busy && !genGuard.current[key]) {
      genGuard.current[key] = true;
      generate(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, loading, completed]);

  const pickGoal = (g) => {
    setEarnGoal(g.id);
    setEarnLabel(g.id === "other" ? "" : g.label);
  };

  const goBack = async () => {
    if (stepIdx === 0) return;
    const prev = stepIdx - 1;
    setStepIdx(prev);
    const canonIdx = STEPS.indexOf(steps[prev]);
    if (canonIdx >= 2) api.post("/journey/goto", { step: canonIdx }).catch(() => {});
  };

  const submitBasics = async () => {
    setBusy(true);
    try {
      await api.post("/journey/basics", {
        earn_goal: earnGoal,
        earn_goal_label: earnGoal === "other" ? customGoal : earnLabel,
        audience,
        affiliate_product: affiliateProduct,
        affiliate_problem: affiliateProblem,
        affiliate_link: affiliateLink,
      });
      setData((d) => ({
        ...d, earn_goal: earnGoal, audience,
        affiliate_product: affiliateProduct, affiliate_problem: affiliateProblem,
        affiliate_link: affiliateLink,
      }));
      setStepIdx(steps.indexOf("opportunity"));
    } finally { setBusy(false); }
  };

  const goNext = async () => {
    if (stepKey === "earn") {
      const changed = earnGoal !== (data.earn_goal || "");
      if (changed && downstreamExists("earn")) { setPendingRegen({ fromStep: "earn", isAi: false }); return; }
      setData((d) => ({ ...d, earn_goal: earnGoal }));
      setStepIdx(1);
      return;
    }
    if (stepKey === "audience") {
      // Affiliate path detours to a dedicated product step before generating.
      if (earnGoal === "promote_affiliate") { setStepIdx(steps.indexOf("affiliate")); return; }
      if (audience.trim() !== (data.audience || "") && downstreamExists("audience")) {
        setPendingRegen({ fromStep: "audience", isAi: false }); return;
      }
      await submitBasics();
      return;
    }
    if (stepKey === "affiliate") {
      const audienceChanged = audience.trim() !== (data.audience || "");
      const affChanged =
        affiliateProduct.trim() !== (data.affiliate_product || "") ||
        affiliateProblem.trim() !== (data.affiliate_problem || "") ||
        affiliateLink.trim() !== (data.affiliate_link || "");
      const fromStep = audienceChanged ? "audience" : "affiliate";
      if ((audienceChanged || affChanged) && downstreamExists(fromStep)) {
        setPendingRegen({ fromStep, isAi: false }); return;
      }
      await submitBasics();
      return;
    }
    if (stepKey === "offer") {
      setBusy(true);
      try {
        const { data: res } = await api.post("/journey/complete", { origin_url: window.location.origin });
        setEmailSent(!!res?.email_sent);
        await Promise.all([refreshProfile(), refreshActiveProject()]);
        setCompleted(true);
        celebrate("journey_complete");
      } finally { setBusy(false); }
      return;
    }
    const next = stepIdx + 1;
    const canonIdx = STEPS.indexOf(steps[next]);
    if (canonIdx >= 0) api.post("/journey/goto", { step: canonIdx }).catch(() => {});
    setStepIdx(next);
  };

  // Regenerate an AI step from its own "Regenerate" button — confirm first if
  // later steps depend on it (they'd go stale).
  const requestRegenerate = (targetStepKey) => {
    if (downstreamExists(targetStepKey)) setPendingRegen({ fromStep: targetStepKey, isAi: true });
    else generate(targetStepKey);
  };

  // Steps that will be regenerated if the user confirms the pending edit.
  const affectedSteps = pendingRegen
    ? steps.slice(steps.indexOf(pendingRegen.fromStep) + 1)
        .filter((k) => AI_STEPS.includes(k) && !!data[k])
        .map((k) => STEP_META[k].label)
    : [];

  const applyRegen = async () => {
    const pr = pendingRegen;
    setPendingRegen(null);
    if (!pr) return;
    // AI steps strictly after the edited step — these must be cleared + allowed to re-run.
    const cleared = steps.slice(steps.indexOf(pr.fromStep) + 1).filter((k) => AI_STEPS.includes(k));
    if (pr.isAi) {
      // Clear downstream immediately so the tracker updates before the (slow) regenerate.
      setData((d) => { const next = { ...d }; cleared.forEach((k) => { delete next[k]; }); return next; });
      cleared.forEach((k) => { genGuard.current[k] = false; });
      await generate(pr.fromStep); // overwrite this step (manages its own busy state)
      api.post("/journey/invalidate", { from_step: pr.fromStep }).catch(() => {});
      return;
    }
    // Input change: persist new answers, clear downstream, resume at opportunity.
    setBusy(true);
    try {
      await api.post("/journey/basics", {
        earn_goal: earnGoal,
        earn_goal_label: earnGoal === "other" ? customGoal : earnLabel,
        audience,
        affiliate_product: affiliateProduct,
        affiliate_problem: affiliateProblem,
        affiliate_link: affiliateLink,
      });
      const { data: inv } = await api.post("/journey/invalidate", { from_step: pr.fromStep });
      setData((d) => {
        const next = {
          ...d, ...inv.data, earn_goal: earnGoal, audience,
          affiliate_product: affiliateProduct, affiliate_problem: affiliateProblem,
          affiliate_link: affiliateLink,
        };
        cleared.forEach((k) => { delete next[k]; });
        return next;
      });
      cleared.forEach((k) => { genGuard.current[k] = false; });
      setStepIdx(steps.indexOf("opportunity"));
    } finally { setBusy(false); }
  };

  // Revert any unsaved input change so a cancelled edit can't later re-trigger a false regen.
  const cancelRegen = () => {
    setPendingRegen(null);
    setEarnGoal(data.earn_goal || "");
    setEarnLabel(data.earn_goal_label || "");
    setCustomGoal(data.earn_goal === "other" ? (data.earn_goal_label || "") : "");
    setAudience(data.audience || "");
    setAffiliateProduct(data.affiliate_product || "");
    setAffiliateProblem(data.affiliate_problem || "");
    setAffiliateLink(data.affiliate_link || "");
  };

  const confirmRegen = () => { confirmingRef.current = true; applyRegen(); };

  const onRegenDialogOpenChange = (openState) => {
    if (openState) return;
    if (confirmingRef.current) { confirmingRef.current = false; return; }
    cancelRegen();
  };

  const enterAdvanced = async (fromCompletion) => {
    await setUiMode("advanced");
    let dest = "/dashboard";
    if (fromCompletion === true) {
      // Coming from the completion transition — land on a contextual dashboard.
      try { sessionStorage.setItem("h2i_from_guided", "1"); sessionStorage.removeItem("h2i_return_to"); }
      catch (e) { console.warn("Could not set guided-continuation flag:", e); }
    } else {
      // Mid-journey toggle — return to the exact screen the user was on before Guided.
      try {
        const r = sessionStorage.getItem("h2i_return_to");
        sessionStorage.removeItem("h2i_return_to");
        if (r && !r.startsWith("/journey") && !r.startsWith("/login") && !r.startsWith("/register")) dest = r;
      } catch (e) { console.warn("Could not read return-to path from sessionStorage:", e); }
    }
    navigate(dest);
    // Refresh in the background — a failed refresh must never block the switch.
    Promise.all([refreshProfile(), refreshActiveProject(), refreshCredits()]).catch(() => {});
  };

  const startNewJourney = async () => {
    setLoading(true);
    try {
      await api.post("/journey/reset", { name: "New project", goal: earnGoal || "build_audience" });
      setCompleted(false);
      await loadJourney();
    } finally { setLoading(false); }
  };

  const canContinue =
    (stepKey === "earn" && earnGoal && (earnGoal !== "other" || customGoal.trim())) ||
    (stepKey === "audience" && audience.trim()) ||
    (stepKey === "affiliate" && affiliateProduct.trim() && affiliateProblem.trim()) ||
    (AI_STEPS.includes(stepKey) && !!data[stepKey] && !busy);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 size={30} className="animate-spin text-primary" />
      </div>
    );
  }

  // ---------- Sample / example journey ----------
  const useSampleInputs = (d) => {
    const g = EARN_GOALS.find((x) => x.id === d.goalId);
    setEarnGoal(d.goalId || "");
    setEarnLabel(d.goalId === "other" ? "" : (g ? g.label : ""));
    if (d.goalId === "other") setCustomGoal(d.goalLabel || "");
    setAudience(d.audience || "");
    setSampleMode(false);
    setStepIdx(0);
    toast.success("Example loaded — review the goal & audience, then continue when you're ready.");
  };
  if (sampleMode) {
    return <SampleJourney onExit={() => { setSampleMode(false); setStepIdx(0); }} onUseInputs={useSampleInputs} />;
  }

  // ---------- Journey complete summary ----------
  if (completed) {
    const opp = data.opportunity || {};
    const hook = data.hook || {};
    const pt = data.pitch_test || {};
    const cp = data.content_plan || {};
    const offer = data.offer || {};
    const ads = data.ads || null;
    const saveOfferPrice = async () => {
      setSavingPrice(true);
      try {
        const { data: r } = await api.post("/journey/update-offer", { pricing: priceInput });
        setData((d) => ({ ...d, offer: r.offer }));
        setEditingPrice(false);
        toast.success("Pricing updated");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't save your pricing. Try again.");
      } finally {
        setSavingPrice(false);
      }
    };
    return (
      <div className="min-h-screen bg-background">
        <TopBar onAdvanced={enterAdvanced} showProgress={false} />
        <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
          <JourneyProgress steps={steps} stepIdx={stepIdx} data={data} completed
            onEdit={(k) => jumpToStep(stepForMilestone(k))} />
          <div className="fade-up flex flex-col items-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/15 text-accent">
              <PartyPopper size={28} />
            </div>
            <h1 className="mt-4 font-head text-4xl font-bold tracking-tight sm:text-5xl">
              Your plan is ready{userName ? `, ${userName}` : ""}.
            </h1>
            <p className="mt-2 max-w-lg text-muted-foreground">
              Everything below has been saved to your account — your hook, script, content plan, offer and ads are all here whenever you need them. When you're ready, Advanced Mode lets you go deeper: publish, schedule, build funnels and more.
            </p>
            {emailSent && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1.5 text-sm font-medium text-accent"
                data-testid="journey-email-sent">
                <Mail size={14} /> We've emailed a copy of your plan to your inbox.
              </p>
            )}
          </div>

          <div className="mt-8 space-y-4">
            <SummaryCard icon={Compass} title="Your opportunity" onEdit={() => jumpToStep("opportunity")}>
              <p className="font-semibold">{opp.headline}</p>
              <p className="mt-1 text-sm text-muted-foreground">{opp.selling}</p>
            </SummaryCard>
            <SummaryCard icon={Lightbulb} title="Your hook" copyText={hook.hook} onEdit={() => jumpToStep("hook")} canvaType="social" canvaText={hook.hook}>
              <p>{hook.hook}</p>
            </SummaryCard>
            <SummaryCard icon={Megaphone} title="Pitch test" onEdit={() => jumpToStep("pitch_test")} canvaType="presentation"
              canvaText={[pt.headline, pt.pitch].filter(Boolean).join("\n\n")}>
              <p className="font-semibold">{pt.headline}</p>
              <p className="mt-1 text-sm text-muted-foreground">{pt.pitch}</p>
            </SummaryCard>
            <SummaryCard icon={CalendarDays} title="7-day content plan" onEdit={() => jumpToStep("content_plan")} canvaType="doc"
              canvaText={(cp.days || []).map((d) => `Day ${d.day} — ${d.theme}: ${d.idea}`).join("\n")}>
              <ul className="space-y-1 text-sm">
                {(cp.days || []).map((d) => (
                  <li key={d.day}><span className="font-semibold">Day {d.day}:</span> {d.idea}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard icon={Package} title="Your packaged offer"
              copyText={`${offer.name}\n${offer.promise}\nPricing: ${offer.pricing}\n\n${offer.pitch}`}
              onEdit={() => jumpToStep("offer")} canvaType="presentation"
              canvaText={[offer.name, offer.promise, offer.pricing ? `Pricing: ${offer.pricing}` : "", offer.pitch].filter(Boolean).join("\n\n")}>
              <p className="font-semibold">{offer.name}</p>
              <p className="mt-0.5 text-sm">{offer.promise}</p>
              {offer.pricing && !editingPrice && (
                <div className="mt-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suggested pricing for your course offer</span>
                    <button type="button" data-testid="edit-price-btn"
                      onClick={() => { setPriceInput(offer.pricing || ""); setEditingPrice(true); }}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary">
                      <Pencil size={11} /> Edit
                    </button>
                  </div>
                  <span className="text-primary">{offer.pricing}</span>
                  <span className="ml-1 text-xs text-muted-foreground">(what you could charge your customers)</span>
                </div>
              )}
              {editingPrice && (
                <div className="mt-1.5" data-testid="edit-price-form">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suggested pricing for your course offer</span>
                  <Input value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                    data-testid="price-input" placeholder="e.g. £47 one-off, or £19/month"
                    className="mt-1" onKeyDown={(e) => { if (e.key === "Enter") saveOfferPrice(); }} />
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" onClick={saveOfferPrice} disabled={savingPrice} data-testid="save-price-btn"
                      className="rounded-full">
                      {savingPrice ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />}
                      Save price
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingPrice(false)} data-testid="cancel-price-btn"
                      className="rounded-full text-muted-foreground">Cancel</Button>
                  </div>
                </div>
              )}
              <p className="mt-2 text-sm text-muted-foreground">{offer.pitch}</p>
            </SummaryCard>
          </div>

          {/* Ad creation — bonus, before the "where next" decision */}
          <div className="mt-12 border-t border-border pt-8" data-testid="journey-ads-section">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent/15 text-accent">
                <Megaphone size={24} />
              </div>
              <div>
                <h2 className="font-head text-2xl font-bold tracking-tight">Turn it into ads</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ready-to-run ad copy built from your hook, audience and transformation promise — written in each platform's natural style.
                </p>
              </div>
            </div>

            {!ads ? (
              <div className="mt-5 flex flex-col items-start gap-3 rounded-2xl border border-accent/20 bg-accent/5 p-6">
                <p className="text-sm text-muted-foreground">
                  Get three scroll-stopping ads — one each for YouTube, Instagram and TikTok — ready to paste and run.
                </p>
                <Button onClick={() => generate("ads")} disabled={busy} data-testid="journey-generate-ads-btn"
                  className="rounded-full bg-accent px-6 py-5 text-base font-semibold text-white hover:bg-accent/90">
                  {busy
                    ? <><Loader2 size={18} className="mr-1.5 animate-spin" /> Writing your ads…</>
                    : <><Sparkles size={18} className="mr-1.5" /> Create my ads</>}
                </Button>
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {AD_PLATFORMS.map((p) => ads[p.key] ? (
                  <SummaryCard key={p.key} icon={p.icon} title={p.label} copyText={ads[p.key]}
                    canvaType="social" canvaText={ads[p.key]}>
                    <p className="whitespace-pre-line">{ads[p.key]}</p>
                  </SummaryCard>
                ) : null)}
                <div className="flex justify-center">
                  <Button onClick={() => generate("ads")} disabled={busy} variant="ghost" size="sm"
                    data-testid="journey-regen-ads-btn" className="gap-1.5 text-muted-foreground">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Regenerate ads
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Where next — two clear options */}
          <div className="mt-12 border-t border-border pt-8" data-testid="journey-decision">
            <h2 className="font-head text-2xl font-bold tracking-tight">Where to next?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your work is saved. Pick whatever feels right — you can always switch to Advanced Mode later.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button onClick={() => setShowNext(true)} data-testid="journey-show-next-btn"
                className={`rounded-2xl border p-5 text-left transition-colors ${showNext ? "border-accent bg-accent/5" : "border-border hover:border-accent/50 hover:bg-accent/5"}`}>
                <span className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={18} className="text-accent" /> I'm happy with this
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">Show me what to do next with what I built.</span>
              </button>
              <button onClick={() => enterAdvanced(true)} data-testid="journey-enter-advanced-btn"
                className="rounded-2xl border border-primary/40 bg-primary/5 p-5 text-left transition-colors hover:border-primary hover:bg-primary/10">
                <span className="flex items-center gap-2 font-semibold text-primary">
                  <Compass size={18} /> Take me deeper
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">Open Advanced Mode to publish, schedule and build funnels.</span>
              </button>
            </div>

            {showNext && (
              <div className="fade-up mt-6 rounded-2xl border border-accent/20 bg-accent/5 p-6" data-testid="journey-next-steps">
                <h3 className="font-head text-lg font-bold">Your next 3 steps</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">Small, doable actions to turn your plan into real momentum.</p>
                <ol className="mt-4 space-y-3">
                  {whatToDoNext(earnGoal || data.earn_goal).map((s, i) => (
                    <li key={s.title} className="flex gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">{i + 1}</span>
                      <div>
                        <p className="font-semibold">{s.title}</p>
                        <p className="text-sm text-muted-foreground">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                  <Button onClick={() => enterAdvanced(true)} variant="outline" data-testid="journey-next-advanced-btn"
                    className="rounded-full border-primary/30 text-primary hover:bg-primary/5">
                    Change your mind? Open Advanced Mode <ArrowRight size={16} className="ml-1" />
                  </Button>
                  <Button onClick={startNewJourney} variant="ghost" data-testid="journey-new-btn"
                    className="rounded-full text-muted-foreground">
                    Start a new journey
                  </Button>
                </div>
              </div>
            )}

            {!showNext && (
              <div className="mt-4 flex justify-center">
                <Button onClick={startNewJourney} variant="ghost" size="sm" data-testid="journey-new-btn-alt"
                  className="rounded-full text-muted-foreground">
                  Or start a new journey
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Linear journey ----------
  const Meta = STEP_META[stepKey];
  const Icon = Meta.icon;
  return (
    <div className="min-h-screen bg-background">
      <TopBar onAdvanced={enterAdvanced} />

      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6" data-testid="journey-page">
        <JourneyProgress steps={steps} stepIdx={stepIdx} data={data}
          onEdit={(k) => jumpToStep(stepForMilestone(k))} />
        {isFree && (
          <div data-testid="journey-free-banner"
            className="mb-6 flex items-center gap-2.5 rounded-full border border-accent/30 bg-accent/10 px-4 py-2 text-sm text-accent">
            <Gift size={16} /> This is your free journey — no credits used.
          </div>
        )}

        <div className="fade-up mb-6 flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon size={24} />
          </div>
          <div>
            <h1 className="font-head text-3xl font-bold tracking-tight" data-testid="journey-step-title">
              {Meta.title}
            </h1>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary" data-testid="journey-step-indicator">
              Step {stepIdx + 1} of {steps.length}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground" data-testid="journey-step-desc">
              {Meta.desc}
            </p>
          </div>
        </div>

        <Card className="fade-up border-border p-6 sm:p-8" data-testid={`journey-step-${stepKey}`}>
          <StepBody
            stepKey={stepKey} busy={busy} data={data}
            earnGoal={earnGoal} customGoal={customGoal} setCustomGoal={setCustomGoal}
            pickGoal={pickGoal} audience={audience} setAudience={setAudience}
            onSeeExample={() => setSampleMode(true)}
            affiliateProduct={affiliateProduct} setAffiliateProduct={setAffiliateProduct}
            affiliateProblem={affiliateProblem} setAffiliateProblem={setAffiliateProblem}
            affiliateLink={affiliateLink} setAffiliateLink={setAffiliateLink}
            onRegenerate={() => requestRegenerate(stepKey)}
          />
        </Card>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={goBack} disabled={stepIdx === 0 || busy}
            data-testid="journey-back-btn">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <Button onClick={goNext} disabled={!canContinue} data-testid="journey-continue-btn"
            className="rounded-full bg-primary px-7 py-6 text-base font-semibold hover:bg-primary/90">
            {stepKey === "offer" ? "Finish journey" : "Continue"}
            <ArrowRight size={18} className="ml-1.5" />
          </Button>
        </div>
      </div>

      <AlertDialog open={!!pendingRegen} onOpenChange={onRegenDialogOpenChange}>
        <AlertDialogContent data-testid="journey-regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Update this and refresh the later steps?</AlertDialogTitle>
            <AlertDialogDescription>
              {affectedSteps.length > 0 ? (
                <>Changing this answer means these steps will be regenerated so everything stays consistent:{" "}
                  <span className="font-medium text-foreground">{affectedSteps.join(", ")}</span>. This can't be undone.</>
              ) : (
                <>This will regenerate the steps that build on this answer.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="journey-regen-cancel">Keep as is</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRegen} data-testid="journey-regen-confirm">
              Update &amp; regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TopBar({ onAdvanced }) {
  return (
    <div className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur-xl sm:px-6">
      <div className="flex items-center gap-2.5">
        <img src="/logo-mark.png" alt="Hook2Income AI" className="h-8 w-8 object-contain" />
        <span className="font-head text-base font-bold tracking-tight">
          Guided Journey
        </span>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={onAdvanced} data-testid="journey-advanced-toggle"
          className="rounded-full border border-border px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary">
          Advanced Mode
        </button>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, title, children, copyText, onEdit }) {
  return (
    <Card className="fade-up border-border p-5" data-testid={`journey-summary-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon size={15} className="text-primary" /> {title}
        </span>
        <div className="flex items-center gap-1">
          {onEdit && (
            <button type="button" onClick={onEdit} data-testid={`journey-summary-edit-${title.toLowerCase().replace(/\s+/g, "-")}`}
              title={`Edit — ${title}`}
              className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
              <Pencil size={14} />
            </button>
          )}
          {copyText && <CopyBtn id={title.toLowerCase().replace(/\s+/g, "-")} text={copyText} />}
        </div>
      </div>
      <div className="text-sm leading-relaxed">{children}</div>
    </Card>
  );
}

function StepBody({ stepKey, busy, data, earnGoal, customGoal, setCustomGoal, pickGoal, audience, setAudience, onSeeExample, affiliateProduct, setAffiliateProduct, affiliateProblem, setAffiliateProblem, affiliateLink, setAffiliateLink, onRegenerate }) {
  if (stepKey === "earn") {
    return (
      <div className="space-y-3" data-testid="journey-earn">
        <p className="text-sm text-muted-foreground">Pick the outcome you're chasing — I'll build the whole plan around it.</p>
        {onSeeExample && (
          <button type="button" onClick={onSeeExample} data-testid="journey-see-example"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-accent/50 bg-accent/5 p-3 text-sm font-semibold text-accent transition-colors hover:bg-accent/10">
            <Sparkles size={15} /> See an Example first
          </button>
        )}
        <div className="grid gap-2.5">
          {EARN_GOALS.map((g) => (
            <button key={g.id} onClick={() => pickGoal(g)} data-testid={`journey-goal-${g.id}`}
              className={`rounded-xl border p-4 text-left text-sm font-semibold transition-colors ${
                earnGoal === g.id ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
              }`}>{g.label}</button>
          ))}
        </div>
        {earnGoal === "other" && (
          <Input autoFocus data-testid="journey-goal-custom" value={customGoal}
            onChange={(e) => setCustomGoal(e.target.value)}
            placeholder="Describe your earning goal" className="mt-2 text-base" />
        )}
      </div>
    );
  }

  if (stepKey === "audience") {
    return (
      <div className="space-y-3" data-testid="journey-audience">
        <p className="text-sm text-muted-foreground">
          Describe the people you want to reach. Be specific — it makes everything sharper.
          <FieldHint testid="hint-journey-audience" text="Your target audience: the specific group you're creating for — their situation, goal or struggle. Specific beats broad, e.g. 'new mums wanting a home side-income' not 'everyone'." />
        </p>
        <Input autoFocus data-testid="journey-audience-input" value={audience}
          onChange={(e) => setAudience(e.target.value)}
          placeholder="e.g. Busy parents who want a side income" className="text-base" />
      </div>
    );
  }

  if (stepKey === "affiliate") {
    return (
      <div className="space-y-5" data-testid="journey-affiliate">
        <p className="text-sm text-muted-foreground">
          You're promoting someone else's product. Two quick things and I'll write hooks & scripts specific to it — no link needed.
        </p>
        <div>
          <label className="text-sm font-semibold">What product are you promoting?
            <FieldHint testid="hint-journey-product" text="The affiliate product — someone else's product you recommend for a commission. Give its name and what it does." />
          </label>
          <p className="mb-1.5 text-xs text-muted-foreground">
            The product name and what it does.
          </p>
          <Textarea autoFocus data-testid="journey-affiliate-product" value={affiliateProduct}
            onChange={(e) => setAffiliateProduct(e.target.value)} rows={3}
            placeholder="e.g. AcmeNotes — an AI note-taking app for students" className="text-base" />
        </div>
        <div>
          <label className="text-sm font-semibold">What problem does it solve?</label>
          <p className="mb-1.5 text-xs text-muted-foreground">
            The main pain it takes away for your audience.
          </p>
          <Textarea data-testid="journey-affiliate-problem" value={affiliateProblem}
            onChange={(e) => setAffiliateProblem(e.target.value)} rows={3}
            placeholder="e.g. Students waste hours re-writing messy lecture notes before exams" className="text-base" />
        </div>
        <div>
          <label className="text-sm font-semibold">Your affiliate link <span className="font-normal text-muted-foreground">(optional)</span>
            <FieldHint testid="hint-journey-link" text="Your unique tracking link for the product. When someone clicks it and buys, you earn a commission. We add it to every call-to-action." />
          </label>
          <p className="mb-1.5 text-xs text-muted-foreground">
            Paste it once — I'll drop it into every CTA so your posts are ready to publish.
          </p>
          <Input type="url" data-testid="journey-affiliate-link" value={affiliateLink}
            onChange={(e) => setAffiliateLink(e.target.value)}
            placeholder="https://your-affiliate-link.com/…" className="text-base" />
        </div>
      </div>
    );
  }

  if (busy && !data[stepKey]) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="journey-generating">
        <Loader2 size={26} className="animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Your coach is working on this…</p>
      </div>
    );
  }

  const art = data[stepKey] || {};
  const CanvaBtn = <span />;
  const RegenBtn = (
    <button onClick={onRegenerate} disabled={busy} data-testid="journey-regenerate-btn"
      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-60">
      <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Regenerate
    </button>
  );

  if (stepKey === "opportunity") {
    return (
      <div className="space-y-4" data-testid="journey-opportunity-body">
        <div className="flex items-center justify-between">{CanvaBtn}{RegenBtn}</div>
        <h3 className="font-head text-2xl font-bold">{art.headline}</h3>
        <p className="leading-relaxed text-foreground/80">{art.message}</p>
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What you'll sell</p>
          <p className="mt-1 font-medium">{art.selling}</p>
        </div>
      </div>
    );
  }

  if (stepKey === "hook") {
    return (
      <div className="space-y-4" data-testid="journey-hook-body">
        <div className="flex items-center justify-between">{CanvaBtn}{RegenBtn}</div>
        <div className="flex items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <p className="font-medium">{art.hook}</p>
          <CopyBtn id="journey-hook" text={art.hook} />
        </div>
        {(art.alternatives || []).length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">More options</p>
            <div className="mt-2 space-y-2">
              {art.alternatives.map((a, i) => (
                <div key={a || i} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3 text-sm">
                  <span>{a}</span><CopyBtn id={`journey-hook-alt-${i}`} text={a} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (stepKey === "script") {
    const full = `${art.opening}\n\n${art.middle}\n\n${art.payoff}\n\n${art.cta}`;
    return (
      <div className="space-y-4" data-testid="journey-script-body">
        <div className="flex items-center justify-between gap-2">{CanvaBtn}<div className="flex items-center gap-2">{RegenBtn}<CopyBtn id="journey-script" text={full} /></div></div>
        {[["Opening", art.opening], ["Value middle", art.middle], ["Payoff", art.payoff], ["CTA", art.cta]].map(([l, v]) => (
          <div key={l}>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{l}</p>
            <p className="mt-0.5 leading-relaxed">{v}</p>
          </div>
        ))}
      </div>
    );
  }

  if (stepKey === "pitch_test") {
    return (
      <div className="space-y-4" data-testid="journey-pitch-body">
        <div className="flex items-center justify-between">{CanvaBtn}{RegenBtn}</div>
        <div>
          <h3 className="font-head text-xl font-bold">{art.headline}</h3>
          <p className="text-sm text-muted-foreground">{art.subheadline}</p>
        </div>
        <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-secondary/40 p-4">
          <p className="text-sm leading-relaxed">{art.pitch}</p>
          <CopyBtn id="journey-pitch" text={art.pitch} />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Objections handled</p>
          <div className="mt-2 space-y-2">
            {(art.objections || []).map((o, i) => (
              <div key={o.objection || i} className="rounded-lg border border-border p-3 text-sm">
                <p className="font-semibold">{o.objection}</p>
                <p className="mt-0.5 text-muted-foreground">{o.response}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (stepKey === "content_plan") {
    return (
      <div className="space-y-3" data-testid="journey-plan-body">
        <div className="flex items-center justify-between">{CanvaBtn}{RegenBtn}</div>
        {(art.days || []).map((d) => (
          <div key={d.day} className="flex gap-3 rounded-xl border border-border p-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
              {d.day}
            </span>
            <div className="min-w-0">
              <p className="font-semibold">{d.theme}</p>
              <p className="text-sm text-muted-foreground">{d.idea}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{d.format}{d.cta ? ` · ${d.cta}` : ""}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // offer
  const summary = `${art.name}\n${art.promise}\nPricing: ${art.pricing}\n\n${art.pitch}`;
  return (
    <div className="space-y-4" data-testid="journey-offer-body">
      <div className="flex items-center justify-between gap-2">{CanvaBtn}
        <div className="flex items-center gap-2">{RegenBtn}<CopyBtn id="journey-offer" text={summary} /></div>
      </div>
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Offer name</p>
        <h3 className="font-head text-2xl font-bold">{art.name}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm"><Sparkles size={14} className="text-primary" /> {art.promise}</p>
        <p className="mt-2 font-semibold text-primary">{art.pricing}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ready-to-use pitch</p>
        <p className="mt-1 leading-relaxed text-foreground/80">{art.pitch}</p>
      </div>
    </div>
  );
}
